import { useState, useEffect, useRef } from 'react';
import apiClient from '../services/api-client';

/**
 * Custom hook for polling job status.
 *
 * Polls GET /jobs/<job_id> every 1 second until job completes or fails.
 *
 * @param {string} jobId - Job UUID
 * @param {function} getSessionToken - Clerk session token getter
 * @param {function} onComplete - Callback when job completes: (batch_id, total_events) => {}
 * @param {function} onError - Callback when job fails: (error_message) => {}
 *
 * @returns {object} { status, phase, message }
 */
export const useJobPolling = (jobId, getSessionToken, onComplete, onError) => {
  const [status, setStatus] = useState('pending');
  const [phase, setPhase] = useState(null);
  const [message, setMessage] = useState('');
  const [totalEvents, setTotalEvents] = useState(0);

  const pollIntervalRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change (without triggering effect re-run)
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  }, [onComplete, onError]);

  useEffect(() => {
    if (!jobId) {
      console.log('[POLLING] No jobId, skipping polling');
      return;
    }

    console.log('[POLLING] Starting polling for job:', jobId);
    const hasStoppedRef = { current: false }; // Track if we've stopped polling

    const pollJob = async () => {
      // Don't poll if we've already stopped
      if (hasStoppedRef.current) {
        console.log('[POLLING] Already stopped, skipping poll');
        return;
      }

      try {
        console.log('[POLLING] Fetching job status for:', jobId);
        const response = await apiClient.get(
          `/jobs/${jobId}`,
          {},
          getSessionToken
        );

        const job = response.data;
        console.log('[POLLING] Job status:', job.status, 'Phase:', job.phase);

        setStatus(job.status);
        setPhase(job.phase);
        setMessage(job.progress_message || '');
        setTotalEvents(job.total_events || 0);

        // Check for completion
        if (job.status === 'completed') {
          console.log('[POLLING] Job completed! Stopping polling.');
          hasStoppedRef.current = true;
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          onCompleteRef.current(job.batch_id, job.total_events);
          return;
        }

        // Check for failure
        if (job.status === 'failed') {
          console.log('[POLLING] Job failed! Stopping polling.');
          hasStoppedRef.current = true;
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          onErrorRef.current(job.error || 'Job failed');
          return;
        }

      } catch (error) {
        console.error('[POLLING] Error polling job:', error);

        // Check if this is a network error (transient) vs actual API error
        const isNetworkError = error.message?.includes('Failed to fetch') ||
                               error.message?.includes('Network') ||
                               error.name === 'TypeError';

        if (isNetworkError) {
          // Network error - don't stop polling, just log and continue
          console.log('[POLLING] Network error, will retry on next poll');
          return; // Let the next scheduled poll retry
        }

        // Actual error - stop polling
        hasStoppedRef.current = true;
        if (pollIntervalRef.current) {
          clearTimeout(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        onErrorRef.current(error.message || 'Failed to poll job status');
      }
    };

    // Sequential polling - wait for each poll to complete before scheduling next
    const scheduleNextPoll = () => {
      pollIntervalRef.current = setTimeout(async () => {
        await pollJob();
        if (!hasStoppedRef.current) {
          scheduleNextPoll();
        }
      }, 1000);
    };

    // Poll immediately, then schedule sequential polls
    pollJob().then(() => {
      if (!hasStoppedRef.current) {
        scheduleNextPoll();
      }
    });

    // Cleanup on unmount
    return () => {
      console.log('[POLLING] Unmounting, cleaning up timeout');
      hasStoppedRef.current = true;
      if (pollIntervalRef.current) {
        clearTimeout(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [jobId, getSessionToken]);

  return { status, phase, message, totalEvents };
};
